import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Database, ShieldCheck, FileSignature, Network, Lock } from "lucide-react";
import CARTile from "../components/CARTile";

export default function Landing() {
  return (
    <main data-testid="landing-page">
      {/* HERO */}
      <section className="bg-[var(--ink-heading)] text-[var(--bg-primary)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8">
            <p className="eyebrow text-slate-300" data-testid="hero-eyebrow">
              GovDataCierge · Federated AI-ready data governance
            </p>
            <h1
              className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-tight font-light mt-6"
              data-testid="hero-headline"
            >
              Govern what happens<br />after data is discovered.
            </h1>
            <p className="text-lg text-slate-300 mt-8 max-w-2xl leading-relaxed" data-testid="hero-subhead">
              GovDataCierge helps public-sector sponsors manage AI-ready data
              reuse through purpose-bound requests, authority gates, evidence
              boundaries, human review and KATLAS receipts — without moving
              raw sensitive data into a new central store.
            </p>
            <div className="flex flex-wrap items-center gap-6 mt-10">
              <Link
                to="/sponsor"
                className="inline-flex items-center gap-2 bg-[var(--bg-primary)] text-[var(--ink-heading)] px-6 py-3 text-sm font-medium tracking-wide hover:bg-white transition-colors"
                data-testid="cta-sponsor"
              >
                Open sponsor view <ArrowRight size={16} />
              </Link>
              <Link
                to="/requests"
                className="inline-flex items-center gap-2 text-sm text-slate-200 hover:text-white underline underline-offset-4 decoration-slate-500 hover:decoration-white"
                data-testid="cta-followrequest"
              >
                Follow a data reuse request
              </Link>
            </div>
          </div>
          <aside className="lg:col-span-4 lg:border-l lg:border-slate-700 lg:pl-10 flex flex-col justify-end">
            <p className="text-xs text-slate-400 leading-relaxed font-mono-ui">
              A KATLAS Cierge sandbox · synthetic public-sector data only ·
              no taxpayer, citizen or real operational records are used.
            </p>
            <Link
              to="/diagnostics"
              className="mt-6 text-xs text-slate-300 hover:text-white underline underline-offset-4 decoration-slate-600 self-start"
              data-testid="hero-diagnostics-link"
            >
              KATLAS diagnostics →
            </Link>
          </aside>
        </div>
      </section>

      {/* SCENARIO PANEL */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24" data-testid="scenario-panel">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-20">
          <div>
            <p className="eyebrow">The Case</p>
            <span className="hairline my-4 block" />
            <p className="font-display text-2xl md:text-3xl text-[var(--ink-heading)] leading-snug">
              A transformation team wants to reuse a dataset for an AI-assisted
              analysis, but the organisation must prove the purpose, authority,
              evidence boundary and downstream decision path.
            </p>
          </div>
          <div>
            <p className="eyebrow">What GovDataCierge does</p>
            <span className="hairline my-4 block" />
            <p className="font-display text-2xl md:text-3xl text-[var(--ink-heading)] leading-snug">
              GovDataCierge shows what data is being requested, why it is being
              reused, who may approve it, what evidence is shared or withheld,
              and what receipt proves the governed action.
            </p>
          </div>
        </div>
      </section>

      {/* EXECUTIVE FRAMING (Cierge Labs positioning, sponsor-led) */}
      <section className="border-y border-slate-200 bg-white" data-testid="executive-framing">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="eyebrow">Why this exists</p>
            <h2 className="font-display text-3xl md:text-4xl text-[var(--ink-heading)] tracking-tight mt-4 leading-snug">
              Coordinate, not centralise.
            </h2>
          </div>
          <div className="lg:col-span-8 space-y-5 text-[var(--ink-secondary)] text-lg leading-relaxed">
            <p>
              Enterprises and public bodies invest heavily in data lakes,
              integration platforms and governance tooling, yet multi-party
              workflows still fail when organisations cannot share raw data,
              reconciliation remains manual and audit evidence is assembled
              too late.
            </p>
            <p>
              GovDataCierge shows how a lightweight, policy-governed evidence
              layer can sit alongside existing systems — giving operations,
              analytics and AI a shared, auditable stream of governed action
              without centralising sensitive data or replacing existing
              catalogue, graph and metadata tooling.
            </p>
            <p className="text-[var(--ink-mono)] text-base font-mono-ui pt-3 border-t border-slate-200">
              Trusted, low-exposure coordination across organisations, people
              and systems. Faster, cheaper, with less risk — and without
              ripping anything out.
            </p>
          </div>
        </div>
      </section>

      {/* SINGLE VIEW WITHOUT SINGLE DATABASE */}
      <section className="bg-[var(--bg-secondary)] border-y border-slate-200 bg-grain">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <p className="eyebrow">Architecture</p>
              <h2 className="font-display text-3xl md:text-4xl text-[var(--ink-heading)] tracking-tight mt-4 leading-snug">
                A single view<br />without centralising the estate.
              </h2>
            </div>
            <div className="lg:col-span-7">
              <p className="text-[var(--ink-secondary)] leading-relaxed text-lg">
                GovDataCierge does not require sensitive data to be copied into
                a new master database. Each dataset, system, policy owner and
                control point remains under its existing custody. KATLAS
                coordinates governed requests, permissions, evidence references,
                approvals and receipts across those boundaries — creating an
                operational view of what is happening without turning one
                platform into a single point of failure.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16">
            <div className="bg-white border border-slate-200 p-7" data-testid="explainer-1">
              <Database size={22} className="text-[var(--ink-heading)] mb-4" />
              <h3 className="font-display text-xl text-[var(--ink-heading)] leading-snug">
                Data stays where it is
              </h3>
              <p className="text-[var(--ink-secondary)] mt-3 leading-relaxed text-sm">
                Sensitive data remains in source systems, legacy platforms or
                responsible teams. GovDataCierge uses references, metadata,
                evidence hashes and role-based permissions rather than copying
                raw data into a new central store.
              </p>
            </div>
            <div className="bg-white border border-slate-200 p-7" data-testid="explainer-2">
              <ShieldCheck size={22} className="text-[var(--ink-heading)] mb-4" />
              <h3 className="font-display text-xl text-[var(--ink-heading)] leading-snug">
                Authority is checked at the point of action
              </h3>
              <p className="text-[var(--ink-secondary)] mt-3 leading-relaxed text-sm">
                Before data is reused, transformed, accessed by AI or relied on
                in a decision, the system checks purpose, policy, role, lawful
                basis and approval authority.
              </p>
            </div>
            <div className="bg-white border border-slate-200 p-7" data-testid="explainer-3">
              <FileSignature size={22} className="text-[var(--ink-heading)] mb-4" />
              <h3 className="font-display text-xl text-[var(--ink-heading)] leading-snug">
                Receipts create the shared view
              </h3>
              <p className="text-[var(--ink-secondary)] mt-3 leading-relaxed text-sm">
                KATLAS records the governed action: who requested, who approved,
                what evidence was shared or withheld, what action followed and
                what receipt proves the outcome.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CAR SECTION */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24" data-testid="car-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-12">
          <div className="lg:col-span-5">
            <p className="eyebrow">CAR</p>
            <h2 className="font-display text-3xl md:text-4xl text-[var(--ink-heading)] tracking-tight mt-4 leading-snug">
              Custody. Authority. Receipts.
            </h2>
          </div>
          <div className="lg:col-span-7">
            <p className="text-[var(--ink-secondary)] text-lg leading-relaxed">
              Custody controls the evidence. Authority governs the action.
              Receipts prove what happened.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <CARTile
            eyebrow="Custody"
            title="Who controls the evidence"
            body="Custody covers who controls the dataset, metadata, lineage, policy notes, access evidence and withheld material. The custodian decides what may be referenced and what stays local."
            footnote="Dataset · metadata · lineage · withheld material"
          />
          <CARTile
            eyebrow="Authority"
            title="Who may decide"
            body="Authority covers who may request, approve, refuse, escalate, transform or rely on the data. Authority is checked at the point of action, not after."
            footnote="Request · approve · refuse · escalate · rely"
          />
          <CARTile
            eyebrow="Receipts"
            title="What proves it"
            body="Receipts prove the governed action without exposing raw sensitive data. The KATLAS receipt references the actor, role, policy, evidence boundary and outcome."
            footnote="Signed by the live KATLAS node"
          />
        </div>
      </section>

      {/* KATLAS SECTION */}
      <section className="bg-[var(--ink-heading)] text-[var(--bg-primary)]" data-testid="katlas-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="eyebrow text-slate-400">KATLAS</p>
            <h2 className="font-display text-3xl md:text-4xl tracking-tight mt-4 leading-snug">
              Why KATLAS sits underneath.
            </h2>
            <Network size={28} className="mt-8 text-slate-400" />
          </div>
          <div className="lg:col-span-7 space-y-6 text-slate-300 leading-relaxed text-lg">
            <p>
              KATLAS does not replace the data catalogue, knowledge graph,
              analytics platform or AI tool. It governs the actions around
              them: who requested data reuse, who had authority, what purpose
              was declared, what evidence was shared or withheld, what AI was
              allowed to suggest, and what receipt proves the outcome.
            </p>
            <p>
              KATLAS does not create another central database. It creates
              governed connections between people, systems, policies, controls
              and evidence. That allows a sponsor to see the operational
              picture — what was requested, what was authorised, what was
              used, what was withheld and what changed — while the underlying
              data remains under local custody.
            </p>
            <div className="flex items-center gap-2 pt-4 text-xs text-slate-400 font-mono-ui">
              <Lock size={12} /> No bearer token, raw data or signed envelope ever appears in the public UI.
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
